import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { applyWithdraw, fetchPaymentBinding, fetchWalletSummary, type PaymentBinding } from '@/api/wallet';
import { getToken } from '@/lib/auth-storage';
import { formatBalanceU } from '@/utils/product-display';

export default function WithdrawPage() {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [binding, setBinding] = useState<PaymentBinding | null>(null);
  const [amount, setAmount] = useState('');
  const [remark, setRemark] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!getToken()) {
      navigate('/login?redirect=/withdraw');
      return;
    }
    Promise.all([fetchWalletSummary(), fetchPaymentBinding()])
      .then(([walletRes, bindingRes]) => {
        if (walletRes.code === 0 && walletRes.data) {
          setBalance(Number(walletRes.data.balance || 0));
        }
        if (bindingRes.code === 0 && bindingRes.data) {
          setBinding(bindingRes.data);
        }
      })
      .catch(() => setError('网络异常，请稍后重试'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const hasBinding = !!binding && (!!binding.usdtAddress || (!!binding.bankAccountName && !!binding.bankName && !!binding.bankCardNo));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = Number(amount);
    if (!value || value <= 0) {
      setError('请输入有效提现金额');
      return;
    }
    if (value > balance) {
      setError('提现金额不能超过可用余额');
      return;
    }
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      const res = await applyWithdraw(value, remark);
      if (res.code === 0) {
        setMessage(`提现申请已提交，单号：${res.data?.applyNo || ''}`);
        setAmount('');
        const walletRes = await fetchWalletSummary();
        if (walletRes.code === 0 && walletRes.data) {
          setBalance(Number(walletRes.data.balance || 0));
        }
      } else {
        setError(res.msg || '提交失败');
      }
    } catch {
      setError('网络异常，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f7f8]">
      <Navbar />
      <main className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8 pb-24 md:pb-8">
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-5">
          <Link to="/wallet" className="hover:text-gray-700">我的钱包</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-700 font-medium">提现申请</span>
        </div>

        <div className="bg-gray-900 rounded-2xl p-5 sm:p-6 mb-5 text-white">
          <div className="text-sm text-white/60 mb-2">可用余额</div>
          <div className="text-3xl font-bold">{formatBalanceU(balance)}</div>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6">
          <h1 className="text-xl font-bold text-gray-900 mb-1">提交提现订单</h1>

          {loading ? (
            <div className="py-10 text-center text-sm text-gray-400">加载中…</div>
          ) : (
            <div className="space-y-4">
              {!hasBinding && (
                <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-700">
                  请先绑定 U 地址或银行卡信息。
                  <Link to="/payment-binding" className="ml-1 font-semibold underline">去绑定</Link>
                </div>
              )}

              <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-600 space-y-1">
                <div>U地址：{binding?.usdtAddress || '未绑定'}</div>
                <div>银行卡：{binding?.bankName || '未绑定'} {binding?.bankCardNo || ''}</div>
              </div>

              <label className="block">
                <span className="text-sm font-medium text-gray-700">提现金额</span>
                <input
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  inputMode="decimal"
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-gray-400"
                  placeholder="请输入提现金额"
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">备注</span>
                <textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  rows={3}
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-gray-400 resize-none"
                  placeholder="可选"
                />
              </label>

              {message && <div className="text-sm text-emerald-600">{message}</div>}
              {error && <div className="text-sm text-red-500">{error}</div>}

              <button
                type="submit"
                disabled={submitting || !hasBinding}
                className="w-full sm:w-auto px-6 py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-700 disabled:opacity-60"
              >
                {submitting ? '提交中…' : '提交提现申请'}
              </button>
            </div>
          )}
        </form>
      </main>
      <Footer />
    </div>
  );
}
